import React from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { createPageUrl } from '../utils';
import { motion } from 'framer-motion';
import { Loader2, Compass } from 'lucide-react';

export default function InteractiveView() {
  const { data: projects = [], isLoading } = useQuery({
    queryKey: ['projects-3d'],
    queryFn: async () => {
      const allProjects = await base44.entities.Project.list('-created_date');
      return allProjects.filter(p => p.glb_file_data);
    },
    initialData: []
  });

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-20 px-6 md:px-12 lg:px-20">
        <div className="max-w-[1800px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-light text-[#2d2d2d] mb-6">
              Visualizzatore 3D
            </h1>
            <div className="w-16 h-px bg-[#9c8b7a] mx-auto mb-6" />
            <p className="text-[#7a7a7a] text-lg leading-relaxed">
              Esplora i nostri progetti in modalità immersiva 3D. 
              Muoviti liberamente all'interno degli spazi e scopri ogni dettaglio.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="px-6 md:px-12 lg:px-20 pb-24">
        <div className="max-w-[1800px] mx-auto">
          {isLoading ? (
            <div className="flex justify-center items-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-[#9c8b7a]" />
            </div>
          ) : projects.length === 0 ? (
            <div className="text-center py-20">
              <Compass className="w-16 h-16 text-[#9c8b7a] mx-auto mb-4 opacity-50" />
              <p className="text-[#7a7a7a] text-lg">
                Nessun progetto con visione interattiva disponibile al momento.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {projects.map((project, index) => (
                <motion.div
                  key={project.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.6 }}
                >
                  <Link 
                    to={createPageUrl('ProjectViewer3D') + '?id=' + project.id}
                    className="group block"
                  >
                    <div className="relative aspect-[4/3] overflow-hidden mb-4">
                      <img
                        src={project.cover_image}
                        alt={project.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                        <div className="bg-white/90 backdrop-blur-sm rounded-full p-4">
                          <Compass className="w-8 h-8 text-[#9c8b7a]" />
                        </div>
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-serif text-[#2d2d2d] mb-2 group-hover:text-[#9c8b7a] transition-colors">
                        {project.title}
                      </h3>
                      {project.short_description && (
                        <p className="text-sm text-[#7a7a7a] line-clamp-2">
                          {project.short_description}
                        </p>
                      )}
                      <div className="flex items-center gap-2 mt-3 text-sm text-[#9c8b7a]">
                        <Compass className="w-4 h-4" />
                        <span>Esplora in 3D</span>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}